import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Network, Tag, Info, Database, Search, Shield, Radio, Clock, ExternalLink, Copy, ChevronRight, Maximize2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { AssetDetailTabs } from '../../components/assets/AssetDetailTabs';
import { AssetGraph } from '../../components/graphs/AssetGraph';
import { TechnologyStack } from '../../components/assets/TechnologyStack';
import { AssetKeyInfo } from '../../components/assets/detail/AssetKeyInfo';
import { AssetWhois } from '../../components/assets/detail/AssetWhois';
import { AssetDNSRecords } from '../../components/assets/detail/AssetDNSRecords';
import { AssetServices } from '../../components/assets/detail/AssetServices';
import { AssetTLSCertificate } from '../../components/assets/detail/AssetTLSCertificate';
import { AssetRecentFindings } from '../../components/assets/detail/AssetRecentFindings';
import { AssetPorts } from '../../components/assets/detail/AssetPorts';
import { AssetFindings } from '../../components/assets/detail/AssetFindings';
import { AssetEvents } from '../../components/assets/detail/AssetEvents';
import { AssetNotes } from '../../components/assets/detail/AssetNotes';
import { AssetRiskScoreCard } from '../../components/assets/detail/AssetRiskScoreCard';
import { AssetsApi, IPAddress, Port, PaginatedPortList, FindingsApi, TechnologiesApi, Finding, Technology, LogEntry } from 'api-client';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';
import * as Tooltip from '@radix-ui/react-tooltip';

const parseJsonData = (data: string | null): any => {
  try {
    return JSON.parse(JSON.stringify(data) || '{}');
  } catch (error) {
    console.log(error)
    return {};
  }
};

// API will be initialized with auth token


interface IpData {
  ipAddress: IPAddress | null;
  findings: {
    results: Finding[];
    count: number;
    next: string | null;
    previous: string | null;
  };
  technologies: Technology[] | null;
  ports: Port[] | null;
  entries: LogEntry[];
  loading: boolean;
  error: string | null;
}

const initialIpData: IpData = {
  ipAddress: null,
  findings: {
    results: [],
    count: 0,
    next: null,
    previous: null
  },
  technologies: null,
  ports: null,
  entries: [],
  loading: true,
  error: null
};



const generateAssetGraphData = (ipAddress: IPAddress | null, data: IpData) => {
  if (!ipAddress) return { nodes: [], links: [] };
  
  const nodes = [
    { id: 'asset', label: ipAddress.ipAddress, type: 'asset', risk: 'medium' }
  ];
  const links: any[] = [];

  // Add port nodes and edges from real data
  console.log(data.ports)
  if (data.ports) {
    data.ports.forEach((port: Port, index) => {
      const portId = `port_${index}`;
      nodes.push({
        id: portId,
        label: `Port ${port.number}`,
        type: 'port',
        risk: 'low'
      });
      links.push({ source: 'asset', target: portId, type: 'has_port' });
    });
  }

  // Add findings as vulnerability nodes
  if (data.findings?.results) {
    data.findings.results.forEach((finding, index) => {
      const findingId = `finding_${index}`;
      nodes.push({
        id: findingId,
        label: finding.title || 'Unknown Finding',
        type: 'vulnerability',
        risk: finding.severity || 'medium'
      });
      links.push({ source: 'asset', target: findingId, type: 'has_vulnerability' });
    });
  }

  return { nodes, links };
};

const IpDetails = () => {
  const { id } = useParams();
  const [data, setData] = useState<IpData>(initialIpData);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);

  useEffect(() => {
    const fetchIpData = async () => {
      if (!id || !currentTeam?.slug || !token) return;

      try {
        const assetsApi = new AssetsApi(getApiConfiguration(token));
        const technologiesApi = new TechnologiesApi(getApiConfiguration(token));

        const [ipResponse, portsResponse, technologiesResponse] = await Promise.all([
          assetsApi.ipAddressesRetrieve({ id, teamSlug: currentTeam.slug }),
          assetsApi.assetsPortsRetrieve({ assetId: parseInt(id), assetType: 'ip_address', teamSlug: currentTeam.slug }),
          technologiesApi.technologiesAssetsList({ assetId: parseInt(id), assetType: 'ip_address', teamSlug: currentTeam.slug })
        ]);

        setData(prev => ({
          ipAddress: ipResponse,
          findings: prev.findings, // Preserve existing findings
          technologies: technologiesResponse,
          ports: portsResponse ? (portsResponse as unknown as PaginatedPortList).results : null, // Wrap single port in array
          entries: prev.entries, // Preserve existing entries
          loading: false,
          error: null
        }));
      } catch (error) {
        console.error('Error fetching IP data:', error);
        setData(prev => ({
          ipAddress: null,
          findings: prev.findings,
          technologies: null,
          ports: null,
          entries: prev.entries, // Preserve existing entries
          loading: false,
          error: error instanceof Error ? error.message : 'An error occurred'
        }));
      }
    };

    fetchIpData();
  }, [id, currentTeam?.slug, token]);

  // Separate effect for fetching findings
  const [findingsFilters, setFindingsFilters] = useState<{
    search: string;
    severity: string | undefined;
    status: string | undefined;
    createdLast: number | undefined;
  }>({
    search: '',
    severity: undefined,
    status: undefined,
    createdLast: undefined
  });

  const handleFilterChange = useCallback((filters: {
    search?: string;
    severity?: string;
    status?: string;
    createdLast?: number;
  }) => {
    setFindingsFilters(prevFilters => ({
      ...prevFilters,
      search: filters.search ?? '',
      severity: filters.severity,
      status: filters.status,
      createdLast: filters.createdLast
    }));
  }, []);

  useEffect(() => {
    const fetchFindings = async () => {
      console.log(ipAddress)
      if (!id || !currentTeam?.slug || !token) return;

      try {
        const findingsApi = new FindingsApi(getApiConfiguration(token));
        const findingsResponse = await findingsApi.assetFindingsList({ 
          assetId: parseInt(id), 
          assetType: 'ip_address', 
          teamSlug: currentTeam.slug,
          page: currentPage,
          ...findingsFilters
        });

        setData(prev => ({
          ...prev,
          findings: {
            results: findingsResponse.results,
            count: findingsResponse.count,
            next: findingsResponse.next || null,
            previous: findingsResponse.previous || null
          }
        }));
      } catch (error) {
        console.error('Error fetching findings:', error);
      }
    };

    fetchFindings();
  }, [id, currentTeam?.slug, token, currentPage, findingsFilters]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  if (data.loading) {
    return <div>Loading...</div>;
  }

  if (data.error || !data.ipAddress) {
    return <div>Error: {data.error || 'IP Address not found'}</div>;
  }

  const ipAddress = data.ipAddress;
  const ports = ipAddress.ports;
  const { results: findings, next: findingsNext, count: findingsCount, previous: findingsPrevious } = data.findings;
  const technologies = data.technologies;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-6">
              {/* Main Content - 2 columns */}
              <div className="col-span-2 space-y-6">
                <AssetKeyInfo data={ipAddress} />
                <AssetRecentFindings findings={findings.slice(0, 3)} />
              </div>

              {/* Services Column */}
              <div className="space-y-6">
                <AssetServices services={ipAddress.ports || []} />
              </div>


            </div>
          </div>
        );
      case 'graph':
        return (
          <div className="space-y-6">
            <div className="bg-[#0F1629] rounded-xl border border-gray-800/50">
              {/* Graph Controls */}
              <div className="p-4 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Button variant="outline" size="sm">
                      <Maximize2 className="h-4 w-4 mr-2" />
                      Zoom to Fit
                    </Button>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-400">Layout:</label>
                      <select className="bg-gray-800/30 border border-gray-800 rounded-lg text-sm text-white px-2 py-1">
                        <option>Force-Directed</option>
                        <option>Hierarchical</option>
                        <option>Circular</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-red-500" />
                      <span className="text-xs text-gray-400">Critical</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-orange-500" />
                      <span className="text-xs text-gray-400">High</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-yellow-500" />
                      <span className="text-xs text-gray-400">Medium</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-emerald-500" />
                      <span className="text-xs text-gray-400">Low</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graph Canvas */}
              <div className="p-6">
                <div className="w-full h-[600px] rounded-lg bg-[#0F1629]">
                  <AssetGraph 
                    nodes={generateAssetGraphData(ipAddress, data).nodes} 
                    links={generateAssetGraphData(ipAddress, data).links}
                    width={800}
                    height={600}
                  />
                </div>
              </div>
            </div>

            {/* Graph Statistics */}
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Graph Statistics</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Total Nodes</span>
                    <span className="text-sm text-white">{generateAssetGraphData(ipAddress, data).nodes.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Total Edges</span>
                    <span className="text-sm text-white">{generateAssetGraphData(ipAddress, data).links.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Connected Components</span>
                    <span className="text-sm text-white">1</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Node Distribution</h3>
                <div className="space-y-4">
                  {['domain', 'ip', 'port', 'service', 'vulnerability', 'certificate'].map(type => (
                    <div key={type} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <span className="text-sm text-gray-400 capitalize">{type}s</span>
                      <span className="text-sm text-white">
                        {generateAssetGraphData(ipAddress, data).nodes.filter(n => n.type === type).length}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Risk Distribution</h3>
                <div className="space-y-4">
                  {['critical', 'high', 'medium', 'low'].map(risk => (
                    <div key={risk} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className={`h-3 w-3 rounded-full ${
                          risk === 'critical' ? 'bg-red-500' :
                          risk === 'high' ? 'bg-orange-500' :
                          risk === 'medium' ? 'bg-yellow-500' :
                          'bg-emerald-500'
                        }`} />
                        <span className="text-sm text-gray-400 capitalize">{risk}</span>
                      </div>
                      <span className="text-sm text-white">
                        {generateAssetGraphData(ipAddress, data).nodes.filter(n => n.risk === risk).length}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'ports':
        return <AssetPorts ports={typeof ipAddress.ports === 'object' ? JSON.parse(JSON.stringify(ipAddress.ports)) : []} />;
      case 'findings':
        return (
          <AssetFindings 
            findings={findings} 
            paginationInfo={{
              count: findingsCount,
              next: findingsNext,
              previous: findingsPrevious
            }}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            itemsPerPage={10}
            onFilterChange={handleFilterChange}
          />
        );
      case 'events':
        return <AssetEvents entries={ipAddress?.history || []} />;
      case 'notes':
        return <AssetNotes objectType="ip_address" objectId={ipAddress.id?.toString()} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Network className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">{ipAddress.ipAddress}</h1>
                {ipAddress.hostname && <Badge variant="active">{ipAddress.hostname}</Badge>}
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Tag className="h-4 w-4" />
                  <div className="flex items-center space-x-2">
                    {ipAddress.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-gray-800/50 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Clock className="h-4 w-4" />
                  <span>Last scan: {new Date(ipAddress.createdAt).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                    onClick={() => {
                      navigator.clipboard.writeText(ipAddress.ipAddress);
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content 
                    id="copy-tooltip"
                    className="bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-md z-50"
                    sideOffset={5}
                  >
                    Copied to clipboard!
                    <Tooltip.Arrow className="fill-gray-800" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
            <a
              href={`https://${ipAddress.ipAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-6 gap-4 mt-6">
          <AssetRiskScoreCard riskScore={ipAddress?.riskScore?.toUpperCase() || ""} findings={ipAddress?.findingsStats} />
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Open Ports</div>
              <Radio className="h-4 w-4 text-emerald-500" />
            </div>
            <div className="text-lg font-medium text-white mt-1">{ipAddress.ports.length}</div>
          </div>
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Active Findings</div>
              <Shield className="h-4 w-4 text-red-500" />
            </div>
            <div className="text-lg font-medium text-white mt-1">{parseJsonData(ipAddress?.findingsStats)?.total}</div>
          </div>
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Techologies</div>
              <Shield className="h-4 w-4 text-red-500" />
            </div>
            <TechnologyStack
                    technologies={ipAddress.technologies}
                    maxVisible={5}
                  />
          </div>
          
 
        </div>
      </div>

      {/* Tabs */}
      <AssetDetailTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        assetId={ipAddress.id?.toString() || ''}
        portsCount={ports.length}
        findingsCount={parseJsonData(ipAddress.findingsStats)?.total || 0}
        eventsCount={ipAddress?.history?.length || 0}
      />

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default IpDetails;